import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import type { DonationCampaign } from '@juvo/schemas';
import { formatUsd } from '@juvo/utils';
import { store } from '../store.js';
import { publicSectionStyles } from '../styles/public.js';
import '../layouts/site-chrome.js';

const TECH_IMG =
  'https://images.unsplash.com/photo-1517420704952-d9f39e95b43e?q=80&w=900&auto=format&fit=crop';
const FUNDS_IMG =
  'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?q=80&w=900&auto=format&fit=crop';
const AMOUNTS = [10, 25, 50];

@customElement('jv-donate-page')
export class JvDonatePage extends LitElement {
  static styles = [
    publicSectionStyles,
    css`
      h1 {
        font-size: var(--jv-fs-display);
        margin: 0 0 0.6rem;
      }
      .intro {
        color: var(--jv-color-muted);
        max-width: 40rem;
        margin-bottom: 2rem;
      }
      .grid {
        display: grid;
        gap: 1.25rem;
        align-items: start;
      }
      @media (min-width: 860px) {
        .grid {
          grid-template-columns: 1.3fr 1fr;
        }
      }
      form {
        display: grid;
        gap: 0.75rem;
        margin-top: 0.5rem;
      }
      .amounts {
        display: flex;
        flex-wrap: wrap;
        gap: 0.45rem;
      }
      .amt {
        font-family: inherit;
        font-size: 0.75rem;
        border: var(--jv-border-w) solid var(--jv-color-border);
        background: var(--jv-color-surface);
        padding: 0.45rem 0.8rem;
        cursor: pointer;
      }
      .amt[aria-pressed='true'],
      .amt:hover {
        background: var(--jv-color-accent);
        color: #fff;
      }
      .stat-row {
        display: flex;
        gap: 1.4rem;
      }
      .stat-num {
        font-size: 1.35rem;
        font-weight: 700;
        color: var(--jv-color-accent);
      }
      .stat-label {
        font-size: 0.7rem;
        color: var(--jv-color-muted);
      }
      .funds-num {
        font-size: 1.85rem;
        font-weight: 700;
        color: var(--jv-color-accent);
        margin: 0;
      }
      .ok {
        color: var(--jv-color-accent);
      }
      .err {
        color: var(--jv-color-danger);
      }
      .lead-badge {
        background: var(--jv-color-amber);
        color: var(--jv-color-ink);
        font-size: 0.65rem;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        padding: 0.25rem 0.6rem;
        border: 1.5px solid var(--jv-color-border);
        justify-self: start;
        width: max-content;
      }
    `,
  ];

  @state() private campaign: DonationCampaign | null = null;
  @state() private tech = {
    name: '',
    email: '',
    phone: '',
    city: '',
    description: '',
    quantity: '1',
  };
  @state() private funds = { name: '', email: '', amountUsd: '25', note: '' };
  @state() private techNote = '';
  @state() private fundsNote = '';
  @state() private error = '';

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribe = store.subscribe(() => this.requestUpdate());
    const amount = new URLSearchParams(location.search).get('amount');
    if (amount && Number(amount) > 0) this.funds = { ...this.funds, amountUsd: amount };
    store.client.getDonationCampaign().then((campaign) => {
      this.campaign = campaign;
    });
  }
  disconnectedCallback() {
    this.unsubscribe?.();
    super.disconnectedCallback();
  }
  private unsubscribe?: () => void;

  render() {
    const t = store.t.bind(store);
    const campaign = this.campaign;
    const amount = Number(this.funds.amountUsd) || 0;
    return html`
      <jv-page>
        <h1>${t('donate.pageTitle')}</h1>
        <p class="intro">${t('donate.pageIntro')}</p>
        <div class="grid">
          <jv-card lead image=${TECH_IMG} media="tall" alt="">
            <div class="tag dollar">${t('donate.techTag')}</div>
            <span class="lead-badge">${t('donate.lead')}</span>
            <h3 class="card-title" style="font-size:1.25rem">${t('donate.techTitle')}</h3>
            <p class="muted">${t('donate.techBody')}</p>
            <div class="stat-row">
              <div>
                <div class="stat-num">${String(campaign?.kgRefurbished ?? 0).padStart(4, '0')}</div>
                <div class="stat-label">${t('donate.kg')}</div>
              </div>
              <div>
                <div class="stat-num">${String(campaign?.devicesThisQuarter ?? 0).padStart(4, '0')}</div>
                <div class="stat-label">${t('donate.devices')}</div>
              </div>
            </div>
            <form @submit=${this.onTech}>
              <jv-form-field
                label=${t('donate.name')}
                name="name"
                required
                .value=${this.tech.name}
                @jv-input=${(e: CustomEvent) => (this.tech = { ...this.tech, name: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.email')}
                name="email"
                type="email"
                required
                .value=${this.tech.email}
                @jv-input=${(e: CustomEvent) => (this.tech = { ...this.tech, email: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.phone')}
                name="phone"
                .value=${this.tech.phone}
                @jv-input=${(e: CustomEvent) => (this.tech = { ...this.tech, phone: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.city')}
                name="city"
                required
                .value=${this.tech.city}
                @jv-input=${(e: CustomEvent) => (this.tech = { ...this.tech, city: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.description')}
                name="description"
                required
                multiline
                .value=${this.tech.description}
                @jv-input=${(e: CustomEvent) =>
                  (this.tech = { ...this.tech, description: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.quantity')}
                name="quantity"
                type="number"
                .value=${this.tech.quantity}
                @jv-input=${(e: CustomEvent) =>
                  (this.tech = { ...this.tech, quantity: e.detail.value })}
              ></jv-form-field>
              <jv-button type="submit">${t('donate.pickup')}</jv-button>
              ${this.techNote ? html`<p class="ok">${this.techNote}</p>` : ''}
            </form>
          </jv-card>

          <jv-card image=${FUNDS_IMG} media="tall" alt="">
            <div class="tag dollar">${t('donate.fundsTag')}</div>
            <h3 class="card-title" style="font-size:1.25rem">${t('donate.fundsTitle')}</h3>
            <p class="muted">${t('donate.fundsBody')}</p>
            <p class="funds-num">${formatUsd(campaign?.fundsRaisedUsd ?? 0, 0)}</p>
            <p class="muted">
              ${t('donate.raisedToward')} ${formatUsd(campaign?.fundsTargetUsd ?? 8000, 0)}
            </p>
            <jv-progress
              tone="amber"
              .value=${campaign?.fundsRaisedUsd ?? 0}
              .max=${campaign?.fundsTargetUsd ?? 8000}
            ></jv-progress>
            <div class="amounts">
              ${AMOUNTS.map(
                (amt) => html`
                  <button
                    class="amt"
                    type="button"
                    aria-pressed=${amount === amt}
                    @click=${() => (this.funds = { ...this.funds, amountUsd: String(amt) })}
                  >
                    $${amt}
                  </button>
                `,
              )}
              <button
                class="amt"
                type="button"
                aria-pressed=${!AMOUNTS.includes(amount)}
                @click=${() => (this.funds = { ...this.funds, amountUsd: '' })}
              >
                ${t('donate.custom')}
              </button>
            </div>
            <form @submit=${this.onFunds}>
              <jv-form-field
                label=${t('donate.amount')}
                name="amount"
                type="number"
                required
                .value=${this.funds.amountUsd}
                @jv-input=${(e: CustomEvent) =>
                  (this.funds = { ...this.funds, amountUsd: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.name')}
                name="fname"
                required
                .value=${this.funds.name}
                @jv-input=${(e: CustomEvent) => (this.funds = { ...this.funds, name: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.email')}
                name="femail"
                type="email"
                required
                .value=${this.funds.email}
                @jv-input=${(e: CustomEvent) =>
                  (this.funds = { ...this.funds, email: e.detail.value })}
              ></jv-form-field>
              <jv-form-field
                label=${t('donate.note')}
                name="note"
                multiline
                .value=${this.funds.note}
                @jv-input=${(e: CustomEvent) => (this.funds = { ...this.funds, note: e.detail.value })}
              ></jv-form-field>
              <jv-button type="submit">${t('donate.now')}</jv-button>
              ${this.fundsNote ? html`<p class="ok">${this.fundsNote}</p>` : ''}
            </form>
          </jv-card>
        </div>
        ${this.error ? html`<p class="err">${this.error}</p>` : ''}
      </jv-page>
    `;
  }

  private onTech = async (event: Event) => {
    event.preventDefault();
    this.error = '';
    try {
      await store.client.submitTechDonation({
        name: this.tech.name,
        email: this.tech.email,
        phone: this.tech.phone || undefined,
        city: this.tech.city,
        description: this.tech.description,
        quantity: Number(this.tech.quantity) || 1,
      });
      this.techNote = store.t('donate.techThanks');
    } catch {
      this.error = store.t('donate.error');
    }
  };

  private onFunds = async (event: Event) => {
    event.preventDefault();
    this.error = '';
    try {
      await store.client.submitFundDonation({
        name: this.funds.name,
        email: this.funds.email,
        amountUsd: Number(this.funds.amountUsd),
        note: this.funds.note || undefined,
      });
      this.fundsNote = store.t('donate.fundsThanks');
    } catch {
      this.error = store.t('donate.error');
    }
  };
}
